import React, { useState, useRef, useEffect, useCallback } from "react";
import { normAnnPriority } from "../../utils/annPriority";
import { apiService, FILES_BASE_URL } from "../../api/apiService";
import {
    FiInfo,
    FiAlertTriangle,
    FiAlertCircle,
    FiShield,
    FiCheck,
    FiUser,
    FiCalendar,
} from "react-icons/fi";
import OknoBramki, { TrescHtml } from "../common/OknoBramki/OknoBramki";
import { htmlDokumentuNaEkran } from "../common/EdytorTresci/TrescDokumentu";
import s from "./AnnouncementModal.module.css";

/**
 * Bramka komunikatu — okno, którego nie da się pominąć, dopóki nie zostanie odczytane.
 *
 * WYGLĄD MA WSPÓLNY z regulaminem (components/common/OknoBramki). Wcześniej oba okna
 * były kopiami tego samego kodu i mimo to różniły się przyciemnieniem tła, rozmyciem,
 * warstwą z-index i szerokością panelu.
 *
 * KOLOR PRIORYTETU NIE JEST JUŻ ZASZYTY W JS. Do 2026-09-07 stał tu literał z czterema
 * gradientami, czterema kolorami i czterema tłami — okno malowało się barwą, która nie
 * miała nic wspólnego ani z motywem, ani z paletą wybraną przez użytkownika. Teraz JS
 * podaje wyłącznie NAZWĘ TONU, a barwę dobiera słownik w OknoBramki.module.css
 * z tokenów (--accent / --color-warning / --color-danger).
 *
 * BRAMKA NIE MA ✕ ANI onClose — to jest zamierzone.
 */

/* Priorytet → ikona, etykieta i NAZWA TONU. Żadnych barw. */
const KONFIG_PRIORYTETU = {
    0:  { etykieta: "Informacja",  ikona: FiInfo,          ton: "info" },
    1:  { etykieta: "Ostrzeżenie", ikona: FiAlertTriangle, ton: "ostrzezenie" },
    2:  { etykieta: "Krytyczne",   ikona: FiAlertCircle,   ton: "krytyczny" },
    99: { etykieta: "RODO",        ikona: FiShield,        ton: "rodo" },
};

const konfigPriorytetu = (priorytet) =>
    KONFIG_PRIORYTETU[priorytet] || KONFIG_PRIORYTETU[0];

const formatujDate = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleString("pl-PL", {
        day: "2-digit",
        month: "long",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
};

/* `onZamknijWPodgladzie` — [polecenie właściciela 2026-09-20] ✕ WYŁĄCZNIE w trybie „Zaloguj jako”
   (Layout podaje je tylko przy aktywnym podglądzie); zamknięcie lokalne, nic nie odhacza. */
const AnnouncementModal = ({ announcement, onRead, onZamknijWPodgladzie = null }) => {
    const [canAccept, setCanAccept] = useState(false);
    const [scrollProgress, setScrollProgress] = useState(0);
    const [isAccepting, setIsAccepting] = useState(false);
    const contentRef = useRef(null);

    /* NORMALIZACJA PRIORYTETU — bez niej bramka RODO była MARTWA. Backend serializuje
       enumy NAZWAMI (globalny JsonStringEnumConverter), więc `priority` przychodzi jako
       "Info"/"Warning"/"Critical"/"Rodo". Porównanie `priority === 99` nie mogło się
       udać nigdy: każdy komunikat brał konfigurację awaryjną (niebieska „Informacja"),
       a RODO nie wymagało przewinięcia treści. Import normAnnPriority stał w tym pliku
       już wcześniej — nieużyty. Ta sama normalizacja pracuje w DashboardPage. */
    const priorytet = normAnnPriority(announcement?.priority);
    const isRodo = priorytet === 99;
    const cfg = konfigPriorytetu(priorytet);

    useEffect(() => {
        if (!announcement) return;
        if (!isRodo) {
            setCanAccept(true);
            setScrollProgress(100);
        } else {
            setCanAccept(false);
            setScrollProgress(0);
        }
    }, [announcement, isRodo]);

    // Sprawdź czy treść wymaga przewijania (RODO)
    useEffect(() => {
        if (!isRodo || !contentRef.current) return;
        const el = contentRef.current;
        // Jeżeli treść nie wymaga przewijania, pozwól akceptować od razu
        if (el.scrollHeight <= el.clientHeight + 5) {
            setCanAccept(true);
            setScrollProgress(100);
        }
    }, [announcement, isRodo]);

    const handleScroll = useCallback(
        (e) => {
            if (!isRodo) return;
            const { scrollTop, scrollHeight, clientHeight } = e.target;
            const maxScroll = scrollHeight - clientHeight;
            if (maxScroll <= 0) {
                setScrollProgress(100);
                setCanAccept(true);
                return;
            }
            const progress = Math.min(100, (scrollTop / maxScroll) * 100);
            setScrollProgress(progress);
            if (scrollTop + clientHeight >= scrollHeight - 5) {
                setCanAccept(true);
            }
        },
        [isRodo]
    );

    const handleAccept = async () => {
        if (!canAccept || isAccepting) return;
        setIsAccepting(true);
        try {
            await apiService.markAnnouncementRead(announcement.id);
            onRead();
        } catch (error) {
            console.error("Błąd akceptacji komunikatu:", error);
        } finally {
            setIsAccepting(false);
        }
    };

    if (!announcement) return null;

    const imageUrl = announcement.imagePath
        ? `${FILES_BASE_URL}/${announcement.imagePath}`
        : null;

    return (
        <OknoBramki
            ton={cfg.ton}
            ikona={cfg.ikona}
            odznaka={cfg.etykieta}
            onZamknijWPodgladzie={onZamknijWPodgladzie}
            tytul={announcement.title}
            meta={[
                { ikona: FiUser, tekst: announcement.authorName },
                { ikona: FiCalendar, tekst: formatujDate(announcement.createdAt) },
            ]}
            postep={isRodo ? scrollProgress : null}
            onScrollTresci={handleScroll}
            refTresci={contentRef}
            podpowiedz={
                isRodo && !canAccept ? "Przewiń do końca, aby zaakceptować" : null
            }
            stopkaInfo={
                isRodo
                    ? { ikona: FiShield, tekst: "Wymagana akceptacja polityki RODO" }
                    : null
            }
            akcja={{
                etykieta: isRodo
                    ? "Zapoznałem się i akceptuję"
                    : "Zapoznałem się z komunikatem",
                ikona: FiCheck,
                onClick: handleAccept,
                zablokowana: !canAccept,
                ladowanie: isAccepting,
            }}
        >
            {imageUrl && (
                <div className={s.obrazek}>
                    <img
                        src={imageUrl}
                        alt="Załącznik"
                        onError={(e) => {
                            e.target.parentElement.style.display = "none";
                        }}
                    />
                </div>
            )}

            {/* [2026-09-08] Ta sama normalizacja i ta sama allowlista, co w centrum powiadomień —
                komunikat wyświetla się identycznie w oknie i na liście. */}
            <TrescHtml html={htmlDokumentuNaEkran(announcement.content)} />
        </OknoBramki>
    );
};

export default AnnouncementModal;
